import { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Network } from "lucide-react";
import { useOpenPartnership, useOpenSandbox } from "@/contexts/ModalContext";

const UrgencyChart = lazy(() => import("@/components/landing/UrgencyChart"));

const UrgencySection = () => {
  const { setOpen: setSandboxOpen } = useOpenSandbox();
  const { setOpen: setPartnershipOpen } = useOpenPartnership();

  return (
    <>
      <section
        id="urgency"
        className="relative py-12 md:py-24 overflow-hidden bg-muted/30"
      >
        <div className="absolute inset-0 bg-grid opacity-[0.12]" />

        <div className="container relative mx-auto px-6">

          {/* Section label */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12 flex justify-center"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-4 py-1.5 text-sm font-medium text-accent">
              <Network className="h-4 w-4" />
              Network Effect
            </span>
          </motion.div>

          {/* ── Two-column layout ── */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Left: argument */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h2 className="text-4xl font-bold text-foreground leading-tight mb-6 md:text-5xl">
                Speculative booking is a restaurant's{" "}
                <span className="text-primary">Saturday night nightmare.</span>
              </h2>

              <div className="space-y-4 text-muted-foreground text-lg leading-relaxed">
                <p>
                  For platforms, it's a churn event.
                </p>
                <p>
                  ClearSlot closes the gap before the table gets held for
                  nothing.
                </p>
              </div>

              <p className="mt-6 text-sm text-muted-foreground">
                One sprint. Three endpoints. The sandbox is live right now.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 flex-wrap">
                <Button
                  variant="hero"
                  className="text-sm group min-h-[44px] md:min-h-0"
                  onClick={() => setSandboxOpen(true)}
                >
                  Try it live
                  <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
                <Button
                  variant="outline"
                  className="text-sm min-h-[44px] md:min-h-0"
                  onClick={() => setPartnershipOpen(true)}
                >
                  Request Partnership
                </Button>
              </div>
            </motion.div>

            {/* Right: recharts network effect chart */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.2 }}
              className="rounded-xl border border-border bg-card/50 p-6"
            >
              {/* Chart header */}
              <div className="mb-5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-0.5">
                  Network signal quality
                </p>
                <p className="text-xs text-muted-foreground">
                  As a function of integrated platforms
                </p>
              </div>

              {/* Y-axis label */}
              <div className="relative">
                <span className="absolute -left-1 top-1/2 -translate-y-1/2 -rotate-90 text-[10px] text-muted-foreground/70 whitespace-nowrap origin-center font-mono">
                  Signal quality
                </span>

                <Suspense fallback={<div className="h-[260px]" />}>
                  <UrgencyChart />
                </Suspense>
              </div>

              {/* Chart footer */}
              <p className="mt-3 font-mono text-[10px] text-muted-foreground/70 text-right">
                Illustrative. Values are representative, not proprietary data.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default UrgencySection;
